import classNames from 'clsx';
import React, { type ReactElement, memo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { DOM_ID_PHONE_HEADER_ERRORS_PAGE } from 'reactApp/appHelpers/advertisingConfigHelpers';
import { disableRestMobileAllDocsFeatures } from 'reactApp/appHelpers/featuresHelpers/features/showAllDocumentsOnMobile';
import DownloadLimitsInformer from 'reactApp/components/DownloadLimitsInformer/DownloadLimitsInformer';
import { usePromoBlock } from 'reactApp/hooks/mobile/usePromoBlock';
import { allDocumentsLoadRequest } from 'reactApp/modules/allDocuments/allDocuments.module';
import { type AllDocumentsSections, EAdditionalSections, EAllDocumentsType } from 'reactApp/modules/allDocuments/allDocuments.types';
import { checkUpdateLicenseRequest } from 'reactApp/modules/profile/profile.module';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { ErrorPage } from 'reactApp/sections/ErrorPage/ErrorPage';
import { ActionsBar, EActionsMode } from 'reactApp/ui/Mobile/ActionsBar/ActionsBar';
import { BreadcrumbsContainer as Breadcrumbs } from 'reactApp/ui/Mobile/Breadcrumbs/BreadcrumbsContainer';
import { Datalist } from 'reactApp/ui/Mobile/Datalist/Datalist';
import { Toolbar } from 'reactApp/ui/Mobile/Toolbar/Toolbar';
import { PageTabMenu } from 'reactApp/ui/PageTabMenu/PageTabMenu';
import { UploadDropArea } from 'reactApp/ui/UploadDropArea/UploadDropArea';
import { UploadListMobileContainer } from 'reactApp/ui/UploadList/UploadListMobile';
import { sendXray } from 'reactApp/utils/ga';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';

import { useRouteChangeProcessing } from '../MainPage/hooks/useRouteChangeProcessing';
import { useGetPageData } from './hooks/useGetPageData';
import styles from './MobileDocumentsPage.css';

export const MobileDocumentsPage = memo(function MobileDocumentsPage(): ReactElement | null {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const promoBlock = usePromoBlock();

    const {
        tabs,
        isDefaultContent,
        statusPage,
        showUploadArea,
        disableContent,
        totalDownloads,
        showDownloadLimitsInformer,
        showUploader,
        showActionsBar,
    } = useGetPageData();

    const { documentType = EAllDocumentsType.document } = (useParams() || {}) as { documentType: AllDocumentsSections };

    const onTabClick = (tab: string) => navigate(`/${tab}`);

    useEffect(() => {
        if (isDefaultContent) {
            dispatch(checkUpdateLicenseRequest());
        }
    }, [isDefaultContent]);

    useEffect(() => {
        if (documentType !== EAdditionalSections.trashbin && documentType !== EAdditionalSections.incoming) {
            dispatch(allDocumentsLoadRequest({ docType: documentType }));
        }
    }, [documentType]);

    useRouteChangeProcessing({ storage: EStorageType.alldocuments, path: location.pathname, search: location.search });

    useEffect(() => {
        sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'page-entered', source: `alldocuments-${documentType}` });
    }, [documentType]);

    useEffect(() => {
        sendXray(`page-enter-docs-mobile-${documentType}`);
    }, []);

    if (disableContent) {
        return null;
    }

    if (statusPage) {
        return (
            <div className={classNames(styles.root, styles.root_error)}>
                <div id={DOM_ID_PHONE_HEADER_ERRORS_PAGE} />
                <Toolbar disableActions />
                <ErrorPage status={statusPage} />
            </div>
        );
    }

    return (
        <>
            <div className={styles.root}>
                <Toolbar showMenu={!disableRestMobileAllDocsFeatures} hideLogo />
                {promoBlock}
                <div className={styles.content}>
                    <Breadcrumbs />
                    <div className={styles.tabs}>
                        <PageTabMenu current={documentType} tabs={tabs} onClick={onTabClick} />
                    </div>

                    {showDownloadLimitsInformer && <DownloadLimitsInformer isMobile totalDownloads={totalDownloads} />}
                    <Datalist />
                </div>
            </div>
            {showActionsBar && <ActionsBar mode={EActionsMode.PRIMARY} />}
            {showUploader && <UploadListMobileContainer />}
            {showUploadArea && <UploadDropArea gaCategory={'mobile'} source="all-docs-mobile" />}
        </>
    );
});
